import axios from 'axios';
import { get, memoize } from 'lodash';
import config from './config';

import Cookies from 'universal-cookie';

const getAuthorizationToken = () => {
  let access_token;
  if (typeof(localStorage) !== 'undefined') {
    access_token = localStorage.getItem('csku.access_token');
  }
  if (!access_token) {
    const cookies = new Cookies();
    access_token = cookies.get('access_token');
  }
  return access_token;
};

const memoizeGet = memoize(
    (url, data = {}) => {
        const cookies = new Cookies();
        const token = getAuthorizationToken();
        return axios.get(url, {
            ...data,
            headers: {
                Authorization: `Bearer ${token}`,
                ...data.headers || {},
            },
            params: {
                assume_login_name: cookies.get('assume_login_name'),
                ...data.params || {},
            },
        });
    },
    (url, data) => JSON.stringify({url, data})
);

export const getProducts = (params) => {
  return memoizeGet(`${config.promostandards_api_url}/products`, { params }).then((response) => {
    return response.data;
  });
};

export const getProductConfigurations = ({ productId }) => {
    return memoizeGet(`${config.promostandards_api_url}/products/${productId}/configuration`).then((response) => {
        return response.data;
    });
};

export const getPart = ({ partId }) => {
    return memoizeGet(`${config.promostandards_api_url}/parts/${partId}`).then((response) => {
        return response.data;
    });
};

export const getParts = (params) => {
    return memoizeGet(`${config.promostandards_api_url}/parts`, {params}).then((response) => {
        return response.data;
    });
};

export const getInventoryLevels = ({ productId, params }) => {
    const url = `${config.promostandards_api_url}/products/${productId}/inventory_levels`;
    return memoizeGet(url, { params: { params: JSON.stringify(params) } }).then((response) => {
        return response.data;
    });
};

export const getCompany = (id) => {
  const url = `${config.promostandards_api_url}/companies/${id}`;
  return memoizeGet(url).then((response) => {
    return response.data;
  });
};

export const getCompanies = (params) => {
    const url = `${config.promostandards_api_url}/companies`;
    return memoizeGet(url, params).then((response) => {
        return response.data;
    });
};

/*
promoSkuArray example:  [
   [{ promo_api_name: 'hit', sku:'1200'}, { promo_api_name: 'hit', sku:'jwye'}, { promo_api_name: 'hit', sku:'11'},
    { promo_api_name: 'alphabroder', sku: '4800a'}, { promo_api_name: 'alphabroder', sku: '1200'}
    ]
*/
export const getValidatedSkusByPromo = async (promoSkuArray) => {
    if (!Array.isArray(promoSkuArray)) {
        promoSkuArray = [promoSkuArray].filter(Boolean);
    }
    if (promoSkuArray.length === 0) {
        return [];
    }
    const allSkus = [
        ...new Set(
            promoSkuArray.map(i=>i.sku).filter(Boolean)
        )
    ];
    
    const url = `${config.promostandards_api_url}/products`;
    const fields = {
        ps_product_id: null,
        promo_api_name: null,
        id: null,
    };
    let response;
    try {
        response = await memoizeGet(url, { params: { sku__in: allSkus.join(','), fields: JSON.stringify(fields) } });
    } catch (error) {
        return {
            ErrorMessage: {
                code: 999,
                description: 'Server not responding',
                error: error.message
            },
        };
    }

    const data = response.data;
    const products = data?.results || [];

    const allowedSet = new Map(promoSkuArray.map(item => [
        `${(item.promo_api_name || '').toLowerCase()}::${item.sku}`,
        item
    ]));

    const upgradableProducts = products.filter(p =>
        allowedSet.has(`${(p.promo_api_name || '').toLowerCase()}::${p.ps_product_id}`)
    ).map(p => {
        const itemData = allowedSet.get(`${(p.promo_api_name || '').toLowerCase()}::${p.ps_product_id}`);
        const { sku, ...otherProps } = itemData;
        return {
            ...p,
            ...otherProps,
        };
    });
    return upgradableProducts;
};

export const checkCredential = async ({ company, username, password }, retry = 3) => {
    const url = `${config.promostandards_api_url}/promo_standards/check_credential`;
    const response = await axios.post(url, {
        company, username, password
    });
    if (!response.data) {
        if (retry > 0) {
            return checkCredential({ company, username, password }, retry - 1);
        } else {
            return {
                ErrorMessage: {
                    code: 999,
                    description: 'Server not responding',
                }
            };
        }
    }
    const code = get(response.data, ['ErrorMessage', 'code']);
    if ([100, 104, 105, 110, 120, 999].indexOf(code) === -1) {
        response.data.ErrorMessage = null;
    }
    return response.data;
};

export const getProduct = async ({ productId, fob_id, currency }) => {
    const response = await memoizeGet(`${config.promostandards_api_url}/products/${productId}`, {
      fob_id, currency
    });
    return response.data;
  };